// Utility to reset all zustand stores in the stores folder to their default state
import { StateCreator, create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { clearSentryUser, setSentryUser } from '../../../sentry.helpers';
import { BranchOffice } from '../models/branch-office.model';
import { User } from '../models/user.model';
import { SUPERVISED_CART_KEY_PREFIX } from '../utils/pos-supervised-cart-storage';
import { DefaultBranchStore, useBranchStore } from './branch.store';
import {
  DefaultBulkProcessStore,
  useBulkProcessStore,
} from './bulk-process.store';
import { DefaultMenuStore, useMenuStore } from './menu.store';
import { DefaultRolStore, useRolStore } from './rol.store';
import { DefaultShiftGateStore, useShiftGateStore } from './shift-gate.store';
import { useLockStore } from './lock.store';
import { getApiBaseUrl } from '@app/shared/utils/env.utils';

export type AuthStoreSlice = {
  isAuthenticated: boolean;
  user: User;
  token: string;
  refreshToken: string;
  defaultBranchOffice?: BranchOffice;
  lastUsername: string;
  hasPinConfigured: boolean;
  hasCardConfigured: boolean;
  /** True when this session was opened via the offline-login fallback
   *  (password / PIN / card verified against cached Argon2 hash) and
   *  we have NOT yet re-authenticated against neoapi. The renderer
   *  surfaces a "Sesión offline" pill so the cashier knows the session
   *  is local-only until reconnect. */
  isOfflineSession: boolean;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setUser: (user: User) => void;
  setToken: (token: string) => void;
  setRefreshToken: (refreshToken: string) => void;
  /**
   * Batched session identity setter — token + refreshToken + user
   * always change together (login, token rotation, impersonation
   * start / restore). Coupled fields must move in one set() so
   * subscribers see a coherent identity, never a half-swapped
   * (new token, old user) frame. See "Batch coupled React state"
   * in apps/neocrm/CLAUDE.md.
   */
  setSession: (session: {
    token: string;
    refreshToken: string;
    user: User;
  }) => void;
  setDefaultBranchOffice: (branchOffice: BranchOffice) => void;
  setLastUsername: (username: string) => void;
  setHasPinConfigured: (value: boolean) => void;
  setHasCardConfigured: (value: boolean) => void;
  setIsOfflineSession: (value: boolean) => void;
  logout: () => void;
};

export const DefaultAuthStore = {
  isAuthenticated: false,
  user: {} as User,
  token: '',
  refreshToken: '',
  defaultBranchOffice: undefined,
  lastUsername: '',
  hasPinConfigured: false,
  hasCardConfigured: false,
  isOfflineSession: false,
};

const authStateSlice: StateCreator<
  AuthStoreSlice,
  [['zustand/devtools', never], ['zustand/persist', unknown]],
  [],
  AuthStoreSlice
> = (set) => ({
  ...DefaultAuthStore,
  setIsAuthenticated: (isAuthenticated: boolean) =>
    set(() => ({ isAuthenticated }), undefined, 'auth/setIsAuthenticated'),
  setUser: (user: User) => {
    // Push identity + tenant tags to Sentry so every future event
    // carries `user.id`, `businessId`, `branchOfficeId`, `role`.
    if (user?.id) {
      const bag = user as unknown as {
        id: string;
        email?: string;
        username?: string;
        businessId?: string;
        branchOfficeId?: string;
        role?: { name?: string } | string;
      };
      setSentryUser({
        id: bag.id,
        email: bag.email,
        username: bag.username,
        businessId: bag.businessId,
        branchOfficeId: bag.branchOfficeId,
        role:
          typeof bag.role === 'string' ? bag.role : bag.role?.name ?? undefined,
      });
    }
    set(() => ({ user }), undefined, 'auth/setUser');
  },
  setToken: (token: string) =>
    set(() => ({ token }), undefined, 'auth/setToken'),
  setRefreshToken: (refreshToken: string) =>
    set(() => ({ refreshToken }), undefined, 'auth/setRefreshToken'),
  setSession: ({ token, refreshToken, user }) => {
    if (user?.id) {
      const bag = user as unknown as {
        id: string;
        email?: string;
        username?: string;
        businessId?: string;
        branchOfficeId?: string;
        role?: { name?: string } | string;
      };
      setSentryUser({
        id: bag.id,
        email: bag.email,
        username: bag.username,
        businessId: bag.businessId,
        branchOfficeId: bag.branchOfficeId,
        role:
          typeof bag.role === 'string' ? bag.role : bag.role?.name ?? undefined,
      });
    }
    // Commit token / refreshToken / user / isAuthenticated in ONE
    // `set()` call so the persist middleware writes a SINGLE blob
    // to `localStorage['auth']`. Previously login fired setSession
    // (writes token+user, isAuthenticated=false) THEN
    // setIsAuthenticated(true) (writes again with the flag), so
    // DevTools observed a microtask-long `{token, isAuthenticated:
    // false}` intermediate blob — visible as the auth key
    // "blipping" false → true during login. Atomic write kills
    // the intermediate frame.
    set(
      () => ({ token, refreshToken, user, isAuthenticated: true }),
      undefined,
      'auth/setSession'
    );
  },
  setDefaultBranchOffice: (branchOffice: BranchOffice) =>
    set(
      () => ({ defaultBranchOffice: branchOffice }),
      undefined,
      'auth/setDefaultBranchOffice'
    ),
  setLastUsername: (lastUsername: string) =>
    set(() => ({ lastUsername }), undefined, 'auth/setLastUsername'),
  setHasPinConfigured: (hasPinConfigured: boolean) =>
    set(() => ({ hasPinConfigured }), undefined, 'auth/setHasPinConfigured'),
  setHasCardConfigured: (hasCardConfigured: boolean) =>
    set(() => ({ hasCardConfigured }), undefined, 'auth/setHasCardConfigured'),
  setIsOfflineSession: (isOfflineSession: boolean) =>
    set(() => ({ isOfflineSession }), undefined, 'auth/setIsOfflineSession'),

  logout: () => {
    clearSentryUser();
    // Best-effort server-side refresh token revocation (fire-and-forget)
    const currentRefreshToken = useAuthStore.getState().refreshToken;
    if (currentRefreshToken) {
      const baseURL = getApiBaseUrl();
      fetch(`${baseURL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
      }).catch(() => {
        // Ignore errors — this is best-effort
      });
    }

    // Reset every in-memory Zustand state FIRST. Each setState
    // fires the persist middleware which synchronously writes the
    // (now-default) blob to localStorage. That's expected — the
    // final `localStorage.clear()` below wipes those writes AND
    // every other persisted key we didn't explicitly reset
    // (`impersonation`, `last-operator`, `scope-filter`,
    // `auth-method-preference`, `permissions-epoch`, ecf-panel
    // caches, etc.). Previous ordering did the clear BEFORE the
    // setState calls, so persist writes AFTER the clear leaked
    // every reset store back into localStorage — visible as
    // "logged out but data still there" (2026-08-14).
    set(() => DefaultAuthStore, undefined, 'auth/logout');
    useLockStore.setState({ isLocked: false, lastActivity: Date.now() });
    useBranchStore.setState(DefaultBranchStore);
    useRolStore.setState(DefaultRolStore);
    useMenuStore.setState(DefaultMenuStore);
    useBulkProcessStore.setState(DefaultBulkProcessStore);
    useShiftGateStore.setState(DefaultShiftGateStore);

    // Preserve a small allowlist across logout — snapshot BEFORE the
    // clear and restore AFTER. These are the ONLY keys that survive
    // logout:
    //   - `table-cols:*` — column-visibility picks, not sensitive,
    //     operators expect them to survive re-login on the same device.
    //   - Supervised-POS cart (`neo-pos-supervised-cart:<userId>`) —
    //     the cashier's in-progress ticket, scoped PER USER so a
    //     different operator never restores someone else's ticket.
    //     localStorage (not sessionStorage) so it survives a full
    //     browser restart; it clears on that user's next sale / cancel
    //     / new ticket.
    const PRESERVE_PREFIXES = ['table-cols:', SUPERVISED_CART_KEY_PREFIX];
    const keep: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (PRESERVE_PREFIXES.some((p) => k.startsWith(p))) {
        const v = localStorage.getItem(k);
        if (v !== null) keep[k] = v;
      }
    }

    sessionStorage.clear();
    localStorage.clear();
    for (const [k, v] of Object.entries(keep)) {
      localStorage.setItem(k, v);
    }
  },
});

export const useAuthStore = create<AuthStoreSlice>()(
  devtools(
    persist(authStateSlice, {
      name: 'auth',
      // Explicit allowlist. Every field the store carries that is
      // transient (in-memory only) MUST be omitted here so persist
      // never writes it back to disk. Bumped version so any prior
      // blob (including the broken `_hasHydrated`-wiped one shipped
      // in the previous fix attempt) is dropped on hydrate and the
      // operator falls back to the login page instead of an empty
      // session.
      version: 1,
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        defaultBranchOffice: state.defaultBranchOffice,
        lastUsername: state.lastUsername,
        hasPinConfigured: state.hasPinConfigured,
        hasCardConfigured: state.hasCardConfigured,
        isOfflineSession: state.isOfflineSession,
      }),
    }),
    { name: 'auth-store' }
  )
);
